import {
  activeFilterCount,
  feedSaleStateFromAuctionStatus,
  fetchHouseIdentity,
  fetchLotPhotos,
  fetchMyLotStandings,
  houseLabel,
  lotSoundFor,
  narrowRoom,
  primaryPhotoUrl,
  resolveStanding,
  setWatched,
  soundLotFromRow,
  parsePriceBound,
  toLiveLot,
  withLiveFigures,
  NO_FILTERS,
  type ClosingWindow,
  type LotFilters,
  type LotSort,
  type LotSound,
  type LotStandingFlags,
  type PublicLotRow,
  fetchSaleHeader,
  saveLiveRoomLayout,
  type SaleHeader as SaleHeaderRow,
} from '@auctisync/api-client';
import type { Tables } from '@auctisync/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { EmptyState, ExposureMeter } from '../../components/bidder-web';
import { LiveStatusBanner } from '../../components/bidder-web/LiveStatusBanner';
import { SaleAside } from '../../components/bidder-web/SaleAside';
import { useLiveSaleAside } from '../../components/bidder-web/useLiveSaleAside';
import { SoundControls } from '../../components/bidder-web/SoundControls';
import {
  DEFAULT_PAGE_SIZE,
  LAYOUT_PAGINATES,
  PAGE_SIZES,
  PAGE_SIZE_STORE_KEY,
  Pager,
  pageAfterSizeChange,
  WEB_LAYOUT_CHIPS,
  WEB_LIVE_LAYOUTS,
  normalizeWebLayoutId,
  type LiveLayoutProps,
  type LiveLot,
  type WebLiveLayoutId,
} from '../../components/bidder-web/liveRoomLayouts';
import { useBidderHouses } from '../../hooks/useBidderHouses';
import { SaleHeader } from '../../components/bidder-web';
import { useConnection } from '../../hooks/useConnection';
import { useLiveClock } from '../../hooks/useLiveClock';
import { useLiveSource } from '../../hooks/useLiveSource';
import { useLotSubscription } from '../../hooks/useLotSubscription';
import { useOnScreenLots } from '../../hooks/useOnScreenLots';
import { useFeedEvent, useSaleFeed } from '../../hooks/useSaleFeed';
import { useSession } from '../../hooks/useSession';
import { fetchEveryRow } from '../../lib/fetchEveryRow';
import { playLotSound } from '../../lib/bidSound';
import { supabase } from '../../lib/supabase';

/**
 * The live room — the sale, as a catalogue AND as it runs. ONE page.
 *
 * 🔴 BROWSE WAS MERGED INTO THIS PAGE (Jim, 2026-09-14): *"All of the things
 * you just put in that table can be accomplished on one page."* and *"There is
 * no need to have Browse and Live."* `/browse` is gone; it now resolves the
 * open sale and lands here. The two pages had drifted into showing the SAME
 * eight lots, the same countdowns, the same bid counts and the same reserve
 * badges, with Live's "Sortable Grid" being Browse's List view under another
 * name. What each had that the other did not was small and additive:
 *
 *   Browse had  → search, the persistent filter rail, the sale switcher.
 *   Live had    → the ten layouts, bid history, standings, the activity feed.
 *
 * Both now live here, so neither is a reason to keep a second page.
 *
 * 🔴 THE FILTER RAIL IS ALWAYS ON, and the standing + activity aside is drawn
 * only for layouts that do not already end in a column of their own — Jim:
 * *"Keep the filter always on and render the right column for the views that
 * need it."* `LAYOUT_HAS_OWN_RAIL` is that rule, keyed so an eleventh layout
 * cannot join without an answer. Without it, Focused Watch would be a rail,
 * beside a layout, beside a rail, beside a rail.
 *
 * 🔴 STANDINGS COUNT THE WHOLE SALE, NOT THE FILTERED VIEW. Narrowing to "no
 * reserve, under $50" must not change how many lots a bidder is winning. The
 * counts read `allLive`; only the layouts read the filtered set.
 *
 * REBUILT to the web design handoff (`web/web-live.jsx`, 2026-09-02) from ten
 * interchangeable layouts down to THREE views.
 *
 * 🔴 WHY TEN BECAME THREE. The ten came from the phone, where only one thing
 * can be on screen: every layout was a different answer to "what do I give up
 * — the run order, the lot, or the activity?". The web gives up nothing, so
 * the question does not arise. Stage shows all three at once; Grid is
 * photograph-led scanning; Table is the dense sortable view a phone genuinely
 * could not carry. Ten arrangements of the same three things was choice for its
 * own sake, and it cost a bidder a decision before they could watch a sale.
 *
 * 🔴 REVERSED 2026-09-14 — THE WEB CARRIES ALL TEN AGAIN. Jim: *"the 10 views
 * must be on the website."* The paragraph above this one argued the web should
 * keep three because "ten arrangements of the same three things was choice for
 * its own sake"; that call is overruled and the reasoning is left standing so
 * nobody re-derives it and quietly cuts them a second time.
 *
 * The consequence is that the web now writes `profiles.live_room_layout` — the
 * SAME preference the app reads — instead of a web-only localStorage key. That
 * was the actual objection to sharing it: a web-only id would have handed the
 * phone a layout it could not render. With the catalogue identical on both
 * surfaces there is no such id, so the choice follows a bidder across devices,
 * which is what the shared column was for.
 *
 * OFFLINE, THE ROOM STOPS CLAIMING TO REPORT. The activity feed pauses with a
 * line saying why rather than showing old bids as though they were current, the
 * header says "Not updating", and every countdown freezes (`data-conn`, in the
 * shell). A live room that keeps animating while disconnected is the single
 * most expensive lie this product could tell.
 *
 * 🔴 EVERY LOT IS LIVE (Jim, 2026-10-07: *"Lot pages have to be live. Bids
 * should update without having to refresh."*). This page loaded its lots ONCE
 * — prices never moved; only the side ticker polled. Now:
 *
 *   - The sale's route decides where live updates come from
 *     (`useLiveSource` → `resolveLiveSource`): the engine's feed for a sale the
 *     engine runs, Realtime for a sale on the Supabase book. Never this page.
 *   - On the engine: ONE summary of every lot when the page opens, then a
 *     message for each lot that changes among those ON SCREEN (the page tells
 *     the engine which, as the bidder scrolls — `useOnScreenLots` →
 *     `setView`), the ones they WATCH, and — signed in — the ones they BID ON.
 *     The ticker is the engine's too. Countdowns run here, on the engine's
 *     clock. Signed-out visitors get the same live page.
 *   - EVERY lot is read and shown — no 500 cap. A DTA sale is 535 lots.
 *   - Sounds play for lots ON SCREEN only, never for the bidder's own bid
 *     (the engine says which bids were theirs), with the same sound control as
 *     the lot page. A gavel when an on-screen lot closes.
 *   - A drop past 30 s during a running sale says "Bidding paused — your bids
 *     are safe" (`LiveStatusBanner`).
 *
 * What each lot shows is the shared `toLiveLot`: the engine's figures over the
 * catalogue row — the SAME mapping the app uses.
 */

type Auction = Pick<
  Tables<'auctions'>,
  'id' | 'slug' | 'title' | 'status' | 'ends_at' | 'buyers_premium_bps' | 'tenant_id' | 'timezone'
>;

// Deliberately no bidder-identity columns: the "you are winning / outbid"
// highlight comes from the get_my_lot_standings RPC (booleans only, compared to
// the caller's own auth.uid() server-side), so anon never receives a bidder id.
type LotRow = Pick<
  Tables<'lots'>,
  | 'id'
  | 'lot_number'
  | 'title'
  | 'current_bid_cents'
  | 'starting_bid_cents'
  | 'bid_count'
  | 'ends_at'
  | 'scheduled_ends_at'
  | 'status'
  | 'category_id'
  | 'inventory_item_id'
  | 'reserve_met'
  | 'has_reserve'
  /* Focused Watch shows the house's condition note under the hero. A real
     column; unlike specs, which live on the inventory item. */
  | 'condition'
  /* 🔴 ADDED FOR THE MERGE. `matchesQuery` reads title AND description, so
     without this column a search for a word that only appears in the body text
     silently finds nothing — the single most common way a search feels broken.
     `LotRow` now structurally satisfies `SearchableLot`, which is what lets the
     shared `searchLots` run over these rows unchanged. */
  | 'description'
>;

const LOT_COLUMNS =
  'id, lot_number, title, description, condition, current_bid_cents, starting_bid_cents, bid_count, ends_at, scheduled_ends_at, status, category_id, inventory_item_id, reserve_met, has_reserve';

/*
 * 🔴 THE 500-LOT CEILING IS GONE (2026-10-08). The page read at most 500 lots
 * and said so ("This sale has more than 500 lots…") — honest, and wrong for a
 * DTA sale of 535. `fetchEveryRow` reads them all, page by page, ordered by
 * lot number then id so no lot repeats or goes missing between pages.
 */

/**
 * 🔴 LOT NUMBER LEADS, and is the default.
 *
 * It was `closing_soonest`, whose comment read *"the RUN ORDER — the order
 * every layout in the design assumes."* True when every layout was time-shaped;
 * not true of a catalogue, where closing-soonest scrambles the lot numbers.
 * The website now opens on "All lots", and a catalogue that opens out of order
 * is not a catalogue. Closing soonest stays, one click away.
 */
const SORTS: { id: LotSort; label: string }[] = [
  { id: 'lot_number', label: 'Lot number' },
  { id: 'closing_soonest', label: 'Closing soonest' },
  { id: 'price_low_high', label: 'Price: low to high' },
  { id: 'most_bids', label: 'Most bids' },
];

const CLOSING: { id: ClosingWindow; label: string }[] = [
  // The same 30 minutes the phone calls "Closing soon" — `CLOSING_SOON_MS`.
  { id: 'next_30_min', label: 'Next 30 minutes' },
  { id: 'next_hour', label: 'Within the hour' },
  { id: 'today', label: 'Today' },
  { id: 'this_sale', label: 'This sale' },
];

/**
 * 🔴 THE SHARED SHAPE, NOT A SECOND COPY. This file declared its own identical
 * `LiveLot` beside the one `liveRoomLayouts` exports — two definitions that
 * only worked because TypeScript is structural, and that would have needed the
 * same three fields added twice in round 2. One definition now — and since
 * 2026-10-08 it is `@auctisync/api-client`'s, the app's too.
 */

/** The room's columns out of a Realtime row (which carries every public column). */
function pickLotRow(r: PublicLotRow): LotRow {
  return {
    id: r.id,
    lot_number: r.lot_number,
    title: r.title,
    description: r.description,
    condition: r.condition,
    current_bid_cents: r.current_bid_cents,
    starting_bid_cents: r.starting_bid_cents,
    bid_count: r.bid_count,
    ends_at: r.ends_at,
    scheduled_ends_at: r.scheduled_ends_at,
    status: r.status,
    category_id: r.category_id,
    inventory_item_id: r.inventory_item_id,
    reserve_met: r.reserve_met,
    has_reserve: r.has_reserve,
  };
}

/** The bidder's standing from `get_my_lot_standings`, before the engine has said. */
function rpcStanding(flags: LotStandingFlags | undefined): 'winning' | 'outbid' | null {
  const s = resolveStanding(flags);
  return s === 'winning' || s === 'outbid' ? s : null;
}

/**
 * The view catalogue is the SHARED one — ten ids, ten labels, one list.
 *
 * `LIVE_ROOM_LAYOUTS` already lived in `@auctisync/api-client`; it moved there
 * from this very app on 2026-08-05 when the phone needed it. The website is
 * simply reading it again. A local copy would be two lists to keep in step, and
 * the typed renderer map in `liveRoomLayouts.tsx` is what makes the build fail
 * if the catalogue ever grows an id the web has not drawn.
 */
/**
 * The WEBSITE's layout id — the shared ten plus `catalogue`.
 *
 * 🔴 The website's default is `catalogue`; the app's stays `reel`. The two
 * surfaces share the stored value, not the default.
 */
type View = WebLiveLayoutId;
const VIEWS = WEB_LAYOUT_CHIPS;
const WEB_DEFAULT_LAYOUT: View = 'catalogue';
/** Session-scoped, so a fresh tab opens on All lots. */
const VIEW_STORE_KEY = 'bidder-live-room-view';

export function PublicLiveRoom() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { session } = useSession();
  const { state: connection } = useConnection();
  const { active, discoverHouse } = useBidderHouses();

  const [auction, setAuction] = useState<Auction | null>(null);
  /** The sale's own detail, for the header. Its own read so a slow or failed
   *  fetch never holds up the live lots, which are the point of this screen. */
  const [saleHeader, setSaleHeader] = useState<SaleHeaderRow | null>(null);
  const [rows, setRows] = useState<LotRow[]>([]);
  const [photos, setPhotos] = useState<Map<string, string>>(new Map());
  const [standings, setStandings] = useState<Map<string, LotStandingFlags>>(new Map());
  const [watchedIds, setWatchedIds] = useState<Set<string>>(new Set());
  // The exposure meter is OPT-IN (`profiles.show_exposure_meter`, default off).
  // Only when the bidder enabled it do we read their own ceilings — and only
  // their own; another bidder's maximum is not readable from a client at all.
  const [meterOn, setMeterOn] = useState(false);
  const [userMax, setUserMax] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  // ── live: where updates come from, the feed, the lots on screen ──
  const { ref: lotsOnScreenRef, ids: onScreenIds } = useOnScreenLots();
  const onScreen = useMemo(() => new Set(onScreenIds), [onScreenIds]);
  const onScreenRef = useRef(onScreen);
  onScreenRef.current = onScreen;
  const { source } = useLiveSource(rows[0]?.id ?? null);
  /** The lots of THIS sale the bidder watches — the engine sends those too. */
  const watchedInSale = useMemo(
    () => rows.filter((r) => watchedIds.has(r.id)).map((r) => r.id),
    [rows, watchedIds],
  );
  const live = useSaleFeed(source, {
    summary: true,
    ticker: true,
    view: onScreenIds,
    watch: watchedInSale,
    initialSaleState: feedSaleStateFromAuctionStatus(auction?.status),
  });
  const feed = live.feed;
  const nowServerMs = useLiveClock(feed);

  // ── the catalogue half: search, filters, categories, the sale switcher ──
  const [query, setQuery] = useState(() => params.get('q') ?? '');
  const [filters, setFilters] = useState<LotFilters>(() => ({
    ...NO_FILTERS,
    // Lot number: the order the house catalogued the sale in, which is what
    // "All lots" means and what the website now opens on.
    sort: 'lot_number',
    // Category filtering is gone (round 2) — Category Columns is the view that
    // answers this. `NO_FILTERS` still carries the empty array the shared
    // predicate expects, so the shared rules are untouched.
    categoryIds: [],
  }));
  const [minRaw, setMinRaw] = useState('');
  const [maxRaw, setMaxRaw] = useState('');

  /**
   * ── Pagination, owned BY THE PAGE ──────────────────────────────────────
   *
   * The page slices before any layout sees the lots, so no layout knows what
   * page it is on and the pager is rendered once. Seven layouts holding their
   * own copy of this would be seven chances to disagree.
   *
   * Page size is device-local rather than an account column: it is a function
   * of the SCREEN, unlike the layout choice, which is a preference about the
   * sale and does follow the bidder across devices.
   */
  const [pageSize, setPageSizeState] = useState<number>(() => {
    try {
      const raw = localStorage.getItem(PAGE_SIZE_STORE_KEY);
      const n = raw === null ? NaN : Number(raw);
      return PAGE_SIZES.some((s) => s === n) ? n : DEFAULT_PAGE_SIZE;
    } catch {
      return DEFAULT_PAGE_SIZE;
    }
  });
  const [page, setPage] = useState(1);

  /**
   * 🔴 SESSION SCOPE, NOT DEVICE SCOPE (round 2). Jim, 2026-09-15: *"ensure All
   * Lots is the default view. It takes me to Gallery View on a fresh load."*
   *
   * Round 1 restored the view from `localStorage` and from the account column,
   * so whatever a bidder last looked at is what they opened the sale in, weeks
   * later. Session scope gives both halves: a fresh tab starts on All lots, and
   * within the visit the choice survives opening a lot and coming back — which
   * unmounts this page, so plain state was not enough either.
   *
   * ⚠️ PAGE SIZE STAYS ON `localStorage`. That one IS a standing preference
   * about the screen; this one is about the visit.
   */
  const [view, setViewState] = useState<View>(() => {
    try {
      return normalizeWebLayoutId(sessionStorage.getItem(VIEW_STORE_KEY)) ?? WEB_DEFAULT_LAYOUT;
    } catch {
      return WEB_DEFAULT_LAYOUT;
    }
  });

  /**
   * Not reporting: the site cannot reach the server, or the engine feed is
   * paused (down past its threshold) or stopped. A short "reconnecting" is not
   * offline — most drops are a blink, and freezing the room over one is worse
   * than the blink (the same rule `ConnectionBar` keeps).
   */
  const feedStatus = live.status;
  const feedDown =
    feedStatus !== null &&
    (feedStatus.status === 'paused' ||
      (feedStatus.status === 'closed' && feedStatus.closedReason !== 'closed_by_caller'));
  const offline = connection === 'offline' || feedDown;

  /**
   * The chosen layout, given everything it needs and nothing it does not.
   *
   * Built here rather than inline so the props are assembled ONCE — ten layouts
   * reading ten slightly different prop bags is how one of them ends up with a
   * stale `nowServerMs` or a missing watch handler.
   */
  function renderLayout() {
    const Layout = WEB_LIVE_LAYOUTS[view];
    const props: LiveLayoutProps = {
      // THIS PAGE of the filtered, sorted run. No layout knows what page it
      // is on — the page slices before handing over, and renders the pager.
      lots: paged,
      /**
       * The filtered run, UNPAGINATED. Watchlist Rail's watched column and
       * Split Compare's pickers read this: both are claims about the BIDDER
       * and the SALE, not about which page is open.
       */
      allLots: shown,
      events: activity,
      nowServerMs,
      offline,
      /**
       * 🔴 A SHORTCUT INTO THE LOT PAGE, NOT A BID. `?bid=1` asks that page to
       * put the caret in the maximum field; it posts nothing and pre-fills no
       * amount. Every money rule — the increment ladder, the secret maximum,
       * the rejection paths — stays where it already lives.
       */
      onBidLot: (lot) => {
        void navigate(`/auctions/${auction?.slug ?? ''}/lots/${String(lot.lotNumber)}?bid=1`);
      },
      onOpenLot: (lot) => {
        // `navigate` returns a promise in react-router v7; nothing here awaits
        // a route change, so it is explicitly discarded rather than floated.
        void navigate(`/auctions/${auction?.slug ?? ''}/lots/${String(lot.lotNumber)}`);
      },
      watchedIds,
      // Null when signed out: the layouts then draw no watch control at all,
      // rather than one that silently does nothing.
      onToggleWatch: session === null ? null : toggleWatch,
    };
    return <Layout {...props} />;
  }

  /**
   * 🔴 WRITES THE SHARED PREFERENCE. Both the device-local key and, when signed
   * in, `profiles.live_room_layout` — the same row the phone reads. A bidder who
   * picks Gallery Wall on the website finds Gallery Wall on their phone, which
   * is the entire reason that column exists.
   *
   * The account write is fire-and-forget: a failed save must not block the view
   * from changing, and the local key has already carried it.
   */
  function setView(next: View) {
    setViewState(next);
    // Layouts show different numbers of lots — page 4 of one is not page 4 of
    // another, so a view change starts over.
    setPage(1);
    try {
      sessionStorage.setItem(VIEW_STORE_KEY, next);
    } catch {
      /* private mode — the choice simply does not survive the lot page */
    }
    /**
     * 🔴 THE ACCOUNT COLUMN IS STILL WRITTEN, THOUGH THE WEB NO LONGER READS IT.
     *
     * Round 2 asked for session scope so a fresh load lands on All lots, and
     * this delivers that. But `profiles.live_room_layout` is the SHARED
     * cross-device preference — the phone reads it, and that is the entire
     * reason the column exists. Dropping the write would have quietly severed
     * the phone from a bidder's choice, which is a bigger change than the one
     * that was asked for, so the write stays and only the READ went.
     *
     * A phone receiving `catalogue` normalizes it away and falls back to its
     * own default, which is the behaviour the web-only id was designed for.
     */
    const uid = session?.user.id;
    if (uid !== undefined) void saveLiveRoomLayout(supabase, uid, next).catch(() => undefined);
  }

  /*
   * 🔴 THE ACCOUNT READ IS GONE (round 2). It was the thing that landed a fresh
   * tab on Gallery Wall: whatever the bidder last picked on any device arrived
   * a beat after paint and replaced All lots. Session scope is the answer to
   * "what was I just looking at"; the account column is not.
   */

  // Re-derive countdowns once per second.
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // 🔴 THE OPEN-SALES READ IS GONE with the Sale group it fed (round 2). It
  // listed the house's other open sales as radios on a page already scoped to
  // one sale — a query and a control that answered a question nobody asked here.

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data: a, error: aErr } = await supabase
        .from('auctions')
        .select('id, slug, title, status, ends_at, buyers_premium_bps, tenant_id, timezone')
        .eq('slug', slug)
        .eq('is_visible', true)
        .maybeSingle();
      if (cancelled) return;
      if (aErr || !a) {
        setError(aErr?.message ?? 'Auction not found.');
        setLoading(false);
        return;
      }
      const sale = a as Auction;
      setAuction(sale);

      void fetchSaleHeader(supabase, sale.slug).then((row) => {
        if (!cancelled) setSaleHeader(row);
      });

      void fetchHouseIdentity(supabase, sale.tenant_id).then((house) => {
        if (!cancelled) discoverHouse(house);
      });

      // EVERY lot, page by page — see `fetchEveryRow`. Ordered by lot number
      // and then id, a unique key, so no lot repeats or is skipped between pages.
      const { rows: lotRows, error: lErr } = await fetchEveryRow<LotRow>((from, to) =>
        supabase
          .from('lots')
          .select(LOT_COLUMNS, from === 0 ? { count: 'exact' } : undefined)
          .eq('auction_id', sale.id)
          .eq('is_visible', true)
          .order('lot_number', { ascending: true })
          .order('id', { ascending: true })
          .range(from, to),
      );
      if (cancelled) return;
      if (lErr !== null) {
        setError(lErr);
        setLoading(false);
        return;
      }
      setRows(lotRows);

      // 🔴 THE CATEGORY READ IS GONE with the filter it fed (round 2). It was a
      // second query on every sale load purely to populate a group of
      // checkboxes; Category Columns is the view that answers that question now.

      // Keyed by LOT id, through the same resolver the native app uses. Keying
      // by inventory item missed every photo attached straight to a lot, so the
      // two surfaces showed different pictures for the same lot.
      const lotPhotos = await fetchLotPhotos(supabase, lotRows);
      if (cancelled) return;
      const map = new Map<string, string>();
      for (const lot of lotRows) {
        const url = primaryPhotoUrl(lotPhotos, lot.id);
        if (url !== null) map.set(lot.id, url);
      }
      setPhotos(map);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, discoverHouse]);

  /**
   * The standing and the activity beside the sale — LIVE, by the rule the lot
   * page and Home share (`useLiveSaleAside`): the database's recent bids once,
   * then the engine's `ticker`; SQL's counts, asked again after the bidder's
   * own standing moves. It moved out of this page on 2026-10-11 so the three
   * pages that draw these panels cannot drift; the layouts' ticker reads the
   * same lines.
   */
  const auctionId = auction?.id;
  const sourceKind = source?.kind ?? null;
  const lotNumberById = useMemo(() => new Map(rows.map((r) => [r.id, r.lot_number])), [rows]);
  const { standing: standingCounts, events: activity } = useLiveSaleAside({
    auctionId: auctionId ?? null,
    feed,
    sourceKind,
    feedStatus,
    offline,
    lotNumbers: lotNumberById,
  });

  /**
   * Sounds — the shared rule (`lotSoundFor`), for lots ON SCREEN at the moment
   * of the change: a bid that was not the bidder's own, or a gavel when one
   * closes. One sound per message, the gavel first: a reconnect that brings
   * several lots up to date says so once, not as a burst.
   */
  useFeedEvent(feed, 'lots', (changes) => {
    if (feed === null) return;
    let sound: LotSound = null;
    for (const { lot, prev } of changes) {
      const s = lotSoundFor({
        prev,
        next: lot,
        onScreen: onScreenRef.current.has(lot.lot_id),
        you: feed.you(lot.lot_id),
      });
      if (s === 'gavel') {
        sound = s;
        break;
      }
      if (s === 'bid') sound = s;
    }
    playLotSound(sound);
  });

  /**
   * A sale on the SUPABASE BOOK: Realtime keeps the rows live, and plays the
   * same sounds by the same rule. "Was it my bid?" cannot come from the engine
   * here; this page places no bids, so a bid from another tab may chime.
   */
  const rowsByIdMap = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);
  const rowsById = useRef(rowsByIdMap);
  rowsById.current = rowsByIdMap;
  const onRealtimeRow = useCallback((incoming: PublicLotRow) => {
    const before = rowsById.current.get(incoming.id);
    if (before === undefined) return;
    if (incoming.is_visible === false) {
      setRows((prev) => prev.filter((r) => r.id !== incoming.id));
      return;
    }
    const next: LotRow = { ...before, ...pickLotRow(incoming) };
    setRows((prev) => prev.map((r) => (r.id === incoming.id ? next : r)));
    playLotSound(
      lotSoundFor({
        prev: soundLotFromRow(before),
        next: soundLotFromRow(next),
        onScreen: onScreenRef.current.has(incoming.id),
        you: undefined,
      }),
    );
  }, []);
  useLotSubscription({
    target: { kind: 'auction', auctionId: auctionId ?? '' },
    onUpdate: onRealtimeRow,
    enabled: sourceKind === 'supabase' && auctionId !== undefined,
  });

  const userId = session?.user.id;
  useEffect(() => {
    if (!userId || auctionId === undefined) {
      setStandings(new Map());
      setWatchedIds(new Set());
      return;
    }
    let cancelled = false;
    void (async () => {
      const [flags, { data: watch }, { data: prof }] = await Promise.all([
        // The shared reader (the app's too). Until the engine's `you` arrives
        // this is the standing; after, `you` wins lot by lot (`toLiveLot`).
        fetchMyLotStandings(supabase, auctionId).catch(() => new Map<string, LotStandingFlags>()),
        supabase.from('bidder_watchlist').select('lot_id'),
        supabase.from('profiles').select('show_exposure_meter').eq('id', userId).maybeSingle(),
      ]);
      if (cancelled) return;

      if (prof?.show_exposure_meter) {
        setMeterOn(true);
        const { data: myBids } = await supabase
          .from('bids')
          .select('lot_id, max_amount_cents')
          .eq('user_id', userId)
          .eq('auction_id', auctionId)
          .eq('is_rejected', false);
        if (cancelled) return;
        const mm = new Map<string, number>();
        for (const b of (myBids ?? []) as { lot_id: string; max_amount_cents: number | null }[]) {
          if (b.max_amount_cents != null) {
            mm.set(b.lot_id, Math.max(mm.get(b.lot_id) ?? 0, b.max_amount_cents));
          }
        }
        setUserMax(mm);
      } else {
        setMeterOn(false);
        setUserMax(new Map());
      }
      setStandings(flags);
      /* Still read whole, because the HEART on each tile needs to know whether
         THIS lot is watched, and that is a per-lot question the counter does not
         answer. It is no longer what the "Watching" figure counts — that was the
         bug: this set spans every sale, so the panel reported the bidder's entire
         watchlist while the same panel on Home reported only this sale's. */
      setWatchedIds(
        new Set((watch ?? []).map((w) => w.lot_id).filter((x): x is string => x !== null)),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, auctionId]);

  const toggleWatch = useCallback(
    (lotId: string) => {
      if (!userId) return;
      const next = !watchedIds.has(lotId);
      setWatchedIds((prev) => {
        const copy = new Set(prev);
        if (next) copy.add(lotId);
        else copy.delete(lotId);
        return copy;
      });
      void setWatched(supabase, userId, lotId, next);
    },
    [userId, watchedIds],
  );

  /**
   * 🔴 THE SHARED MAPPING (`toLiveLot`), NOT THIS PAGE'S. It used to be inline
   * here, a second copy of the app's that had drifted on six questions
   * (2026-10-08). The engine's live view of each lot (`feed.lot`) wins every
   * figure it carries; the catalogue row supplies the rest. The standing is the
   * engine's `you` once it has spoken for a lot, `get_my_lot_standings` until
   * then (`resolveStanding` — `iBid` is the open-lot outbid rule, `otherWon`
   * the closed one).
   */
  const lots: LiveLot[] = useMemo(() => {
    const now = nowServerMs();
    return rows.map((row) =>
      toLiveLot(row, {
        nowMs: now,
        live: feed?.lot(row.id) ?? null,
        // Undefined (the engine has said nothing for this lot) defers to the RPC.
        you: feed?.you(row.id),
        standing: rpcStanding(standings.get(row.id)),
        watched: watchedIds.has(row.id),
        photo: photos.get(row.id) ?? null,
      }),
    );
    // `tick` recomputes countdowns each second; `live.version` re-reads the feed.
  }, [rows, photos, standings, watchedIds, tick, nowServerMs, feed, live.version]);

  /**
   * 🔴 EVERY open lot in the sale, BEFORE search and filters.
   *
   * The standing counts and the "N of M" line both read this. A bidder who
   * narrows to "no reserve, under $50" has not stopped winning the lots outside
   * that filter, and a panel that said so would be lying about money.
   */
  const allLive = useMemo(() => lots.filter((l) => !l.ended), [lots]);

  /*
   * 🔴 THE HEADER HAS NEVER BEEN GIVEN THESE, AND ITS FALLBACK HAS THEREFORE
   * NEVER FIRED. `saleCloseAt` was built with a lot-level fallback precisely
   * because `auctions.ends_at` is unset on plenty of real sales — but this page,
   * the only caller, did not pass `lotEndsAt`, so the band printed no close time
   * while every lot underneath it counted down. Found on 2026-09-18 while wiring
   * the "lots begin closing" calendar entry, which needs the same data.
   *
   * `scheduled_ends_at` is the fallback per lot, matching how `lots` above
   * derives its countdown — one answer to "when does this lot close", not two.
   */

  /**
   * The bidder's own ceilings for the exposure meter: the engine's, where it
   * has said (`you.ceiling_cents` — what is actually bidding for them), the
   * database's otherwise. Same rule as `positionWithYou` on the lot page.
   */
  const liveUserMax = useMemo(() => {
    if (feed === null) return userMax;
    const merged = new Map(userMax);
    for (const [lotId, you] of feed.yours()) {
      if (you.ceiling_cents !== null) merged.set(lotId, you.ceiling_cents);
    }
    return merged;
  }, [userMax, feed, live.version]);

  // Each lot's close as the room shows it (`toLiveLot`: the engine's clock,
  // else `ends_at`, else `scheduled_ends_at`) — one answer, not two.
  const lotEndsAt = useMemo(() => lots.map((l) => l.endsAt), [lots]);

  /**
   * The catalogue rows with the engine's figures laid over them, for the
   * search, filter and sort rules — so "under $50" and "most bids" read the
   * prices and counts on screen, not the ones loaded when the page opened.
   */
  const searchRows = useMemo(
    () => rows.map((row) => withLiveFigures(row, feed?.lot(row.id))),
    // `live.version` re-reads the feed.
    [rows, feed, live.version],
  );

  const priceBounds = useMemo(
    () => ({ minCents: parsePriceBound(minRaw), maxCents: parsePriceBound(maxRaw) }),
    [minRaw, maxRaw],
  );

  /**
   * What the chosen layout is given: searched, filtered, sorted.
   *
   * The rule itself is `narrowRoom` — pure, tested, and deliberately NOT
   * inline: it is the one place that knows the difference between "every open
   * lot" and "the lots matching the rail", and that difference is what keeps
   * the standing counts honest.
   *
   * It moved into `@auctisync/api-client` on 2026-09-21, when the phone merged
   * Browse into its own Live Room and needed the same rule. Nothing about the
   * behaviour here changed — the import is the whole diff.
   */
  const shown = useMemo(
    () => narrowRoom(searchRows, allLive, query, { ...filters, ...priceBounds }, nowServerMs()),
    [searchRows, query, filters, priceBounds, allLive, nowServerMs],
  );

  const activeCount = activeFilterCount({ ...filters, ...priceBounds });
  const narrowed = activeCount > 0 || query.trim() !== '';

  // ── paging over `shown` ─────────────────────────────────────────────────
  const paginates = LAYOUT_PAGINATES[view];
  const pageCount = paginates ? Math.max(1, Math.ceil(shown.length / pageSize)) : 1;

  /**
   * 🔴 CLAMP WHEN THE LIST SHRINKS. Filtering while on page 7 of 7 must not
   * strand a bidder on a page that no longer exists — they would see an empty
   * grid and no way to tell whether the filter matched nothing or the page did.
   */
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const pageStart = (Math.min(page, pageCount) - 1) * pageSize;
  /** What the layout is handed. `shown` itself stays the unpaginated run. */
  const paged = paginates ? shown.slice(pageStart, pageStart + pageSize) : shown;

  /**
   * 🔴 CHANGING PAGE SIZE HOLDS THE FIRST VISIBLE LOT, not the page number. At
   * 100 per page, "page 4" is a completely different part of the sale than it
   * was at 10 — keeping the number would teleport the bidder.
   */
  function setPageSize(next: number) {
    setPageSizeState(next);
    setPage(pageAfterSizeChange(Math.min(page, pageCount), pageSize, next));
    try {
      localStorage.setItem(PAGE_SIZE_STORE_KEY, String(next));
    } catch {
      /* private mode — the choice simply does not persist */
    }
  }

  /** Any narrowing changes what page 1 means, so go back to it. */
  useEffect(() => {
    setPage(1);
  }, [query, filters, minRaw, maxRaw]);

  /*
   * 🔴 THE COUNTS USED TO BE COMPUTED HERE, AND TWO OF THE THREE WERE WRONG.
   *
   * `outbid` counted `l.you === 'outbid'` over OPEN lots, and `you` came from
   * `winning_user_id`, which is written only at close — so it was a hard zero,
   * always. `watching` counted `watchedIds.size`, which is the bidder's ENTIRE
   * watchlist across every sale, so this page and Home showed different numbers
   * for the same sale. Both now come from `get_my_standing_counts`, which
   * applies "open lots in THIS sale" in SQL. See
   * 20260918170000_standing_counts_in_sql.sql.
   */

  function clearFilters() {
    setFilters((f) => ({ ...NO_FILTERS, sort: f.sort }));
    setMinRaw('');
    setMaxRaw('');
    setQuery('');
  }

  if (loading) {
    return (
      <div className="w-wrap" style={{ padding: '48px 0' }}>
        <p className="muted">Loading the live room…</p>
      </div>
    );
  }
  if (error !== null || auction === null) {
    return (
      <div className="w-wrap" style={{ padding: '48px 0' }}>
        <EmptyState
          icon="!"
          title={error ?? 'That sale is not here.'}
          body="It may have been withdrawn, or the link may be wrong."
          action={
            <Link to="/auctions" className="w-btn w-btn-out">
              All auctions
            </Link>
          }
        />
      </div>
    );
  }

  const houseName = active === null ? 'the auction house' : houseLabel(active);
  const guest = session === null;

  return (
    <>
      {/* The engine feed's own state: paused past the threshold during a
          running sale ("Bidding paused — your bids are safe"), a reconnect that
          has lasted, or a feed that stopped. Nothing while live. Times are the
          sale's own zone, named. */}
      <LiveStatusBanner status={feedStatus} timeZone={saleHeader?.timezone ?? auction.timezone} />
      {/* 🔴 THE HEADER STRIP IS OUTSIDE `.w-wrap`, WHICH IS THE ONLY WAY IT
          REACHES THE EDGES. Jim, 2026-09-18: *"See how the header on Home
          reaches all the way across? I want that on the Lots page as well."*

          `.w-wrap` caps at `--maxw` and adds a gutter, so anything inside it
          stops short no matter what CSS it carries. Home's hero has always been
          a sibling of the wrap for exactly this reason, with `.w-hero-in`
          holding the CONTENT to the measure while the band paints edge to edge.
          This is that same arrangement: `.lr-topband` paints, `.lr-top` holds
          the measure. A negative-margin breakout was the alternative and was not
          taken — it has to guess the scrollbar width, and guessing it wrong puts
          a horizontal scrollbar on every page. */}
      <section className="lr-topband">
        {/* 🔴 THE SALE'S INFORMATION, ABOVE EVERYTHING. The same band Browse
          carried (Jim, 2026-09-11), now full width above the rail and the room
          both — it describes the sale, not one column of it.

          It renders only once the detail lands. Until then the fallback below
          keeps the pill and the title, so the page never appears headless while
          a second query is in flight. */}
        {/* 🔴 THE HEADER BAND CARRIES THE STANDING AND THE ACTIVITY (Jim,
          2026-09-15): *"Filter on top and 'Your Standing' and the other box in
          the header on the right side? I want more lots per row."*

          The three-column grid left the lots 932px, which fit three cards and
          often two once gutters were counted. Width spent on a rail and an
          aside is width not spent on lots, and lots are what the page is for.

          This also retires `LAYOUT_HAS_OWN_RAIL`. That rule existed only to
          stop a third column appearing beside the four layouts that already end
          in one — and its cost was that those four showed no standing and no
          activity at all. With both panels up here, every layout gets them and
          nothing is ever three columns deep. */}
        <div className="lr-top">
          {saleHeader !== null ? (
            <SaleHeader
              sale={saleHeader}
              houseName={houseName}
              nowServerMs={nowServerMs}
              offline={offline}
              countLabel={`${String(allLive.length)} lot${allLive.length === 1 ? '' : 's'} open`}
              lotEndsAt={lotEndsAt}
            />
          ) : (
            <div className="wl-hd">
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  {offline ? (
                    <span className="w-pill">Not updating</span>
                  ) : (
                    <span className="w-pill">
                      <span className="w-dot" />
                      Live
                    </span>
                  )}
                  <span className="w-lbl">
                    {allLive.length} lot{allLive.length === 1 ? '' : 's'} open
                  </span>
                </div>
                <h1 className="w-h1" style={{ fontSize: 30 }}>
                  {auction.title}
                </h1>
              </div>
            </div>
          )}

          {/* The standing and the activity feed, beside the sale rather than
              beside the lots.

              🔴 THE MARKUP MOVED OUT (2026-09-18) so Home, the lot page and the
              watchlist can show the same two panels. The feed is what this page
              ALREADY holds — it drives the ticker layouts — so that costs
              nothing. The counts are one added read, and they are worth it: the
              arithmetic they replaced reported a permanent zero for Outbid and
              the bidder's whole cross-sale watchlist for Watching. Both come
              from `useLiveSaleAside` (2026-10-11), the rule Home and the lot
              page share. */}
          <SaleAside
            standing={guest ? null : standingCounts}
            events={activity}
            offline={offline}
            nowServerMs={nowServerMs}
          >
            {meterOn && !guest ? (
              <ExposureMeter
                winningLots={allLive
                  .filter((l) => l.you === 'winning')
                  .map((l) => ({
                    id: l.id,
                    lotNumber: l.lotNumber,
                    title: l.title,
                    current: l.current,
                  }))}
                userMax={liveUserMax}
                premiumBps={auction.buyers_premium_bps}
              />
            ) : null}
          </SaleAside>
        </div>
      </section>

      <div className="w-wrap">
        {allLive.length === 0 ? (
          <div style={{ paddingTop: 22 }}>
            <EmptyState
              icon="⏱"
              title="Every lot in this sale has closed"
              body="Results are on the sale page, and your invoice appears in your account if you won."
              action={
                <Link to={`/auctions/${auction.slug}`} className="w-btn w-btn-out">
                  See the results
                </Link>
              }
            />
          </div>
        ) : (
          /*
          🔴 ONE ARRANGEMENT FOR EVERY LAYOUT. Filters along the top, then the
          lots across the full width. `is-flat` is not a variant a layout opts
          into — there is no second arrangement any more, which is the point:
          the four layouts that used to lose their standing panel now keep it,
          and every layout gets the full page width for lots.
        */
          <div className="lr-page is-flat">
            <div className="lr-head">
              <div className="w-toolbar">
                <div>
                  <p className="muted" style={{ fontSize: 13.5 }}>
                    {narrowed
                      ? `${String(shown.length)} of ${String(allLive.length)} open lots`
                      : `${String(allLive.length)} open lot${allLive.length === 1 ? '' : 's'}`}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* 🔴 THE SEARCH FIELD IS VISIBLE ON THE WEB, not folded behind
                    an icon. Jim asked for a magnifying glass on the PHONE, where
                    the chip row has the only spare space on the screen; the
                    desktop toolbar has room for the field itself, and hiding a
                    control that fits is a tap for nothing. Same rule, different
                    layout — which is the app/website line. */}
                  {/* 🔴 ELEVEN CHIPS BECOME ONE SELECT (round 2). The strip cost a
                    row of vertical space above the lots and put the current view
                    somewhere inside a horizontal scroller; a select states it in
                    one line. `aria-label` is verbatim — the acceptance checks
                    and the design render both address it by that label. */}
                  {/* 🔴 THE SAME SOUND CONTROL AS THE LOT PAGE (Jim, 2026-10-07):
                    sounds play for the lots on screen, and this is where a
                    bidder chooses or mutes them. One component, both pages. */}
                  <SoundControls />
                  <select
                    className="w-chip"
                    style={{ paddingRight: 30 }}
                    value={view}
                    aria-label="Live room view"
                    onChange={(e) => {
                      setView(e.target.value as View);
                    }}
                  >
                    {VIEWS.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.label}
                      </option>
                    ))}
                  </select>
                  <span className="w-field" style={{ minWidth: 200 }}>
                    <span aria-hidden>⌕</span>
                    <input
                      value={query}
                      onChange={(e) => {
                        setQuery(e.target.value);
                        // The query lives in the URL so a narrowed room is a
                        // link a bidder can send or come back to.
                        setParams(
                          (prev) => {
                            const next = new URLSearchParams(prev);
                            if (e.target.value === '') next.delete('q');
                            else next.set('q', e.target.value);
                            return next;
                          },
                          { replace: true },
                        );
                      }}
                      placeholder="Search these lots"
                      aria-label="Search these lots"
                    />
                  </span>
                  <select
                    className="w-chip"
                    style={{ paddingRight: 30 }}
                    value={filters.sort}
                    aria-label="Sort lots"
                    onChange={(e) => setFilters((f) => ({ ...f, sort: e.target.value as LotSort }))}
                  >
                    {SORTS.map((so) => (
                      <option key={so.id} value={so.id}>
                        {so.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* 🔴 THE FILTER ROW LIVES INSIDE `.lr-head` NOW (round 2). It was a
              grid child of `.lr-page.is-flat`, which therefore had three rows;
              with the view select moved into the toolbar the header band owns
              both the toolbar and the filters, and the flat page is two rows.

              🔴 `.lr-filters`, NOT `w-rail w-rail-top` — Jim, 2026-09-15: *"show
              me the filter bar without the dark background."* That class put
              the row in the deep-panel group with the sale header and the
              footer, which is where the dark fill came from. Dropping it drops
              the fill, the repointed muted text and the dark-mode overrides at
              once; the stylesheet gives the row the card shape back.

              🔴 SALE AND CATEGORY GROUPS DELETED. Sale listed the open sales as
              radios on a page already scoped to one sale. Category was a second
              answer to the question Category Columns already asks as a view. */}
              <aside className="lr-filters" aria-label="Filters">
                <div className="w-rail-g">
                  <div className="w-lbl">Closing</div>
                  {CLOSING.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className="w-check"
                      role="radio"
                      aria-checked={filters.closing === c.id}
                      onClick={() =>
                        setFilters((f) => ({ ...f, closing: f.closing === c.id ? 'any' : c.id }))
                      }
                    >
                      <span className="w-box" aria-hidden>
                        ✓
                      </span>
                      {c.label}
                    </button>
                  ))}
                </div>

                <div className="w-rail-g">
                  <div className="w-lbl">Reserve</div>
                  <button
                    type="button"
                    className="w-check"
                    role="checkbox"
                    aria-checked={filters.noReserveOnly}
                    onClick={() => setFilters((f) => ({ ...f, noReserveOnly: !f.noReserveOnly }))}
                  >
                    <span className="w-box" aria-hidden>
                      ✓
                    </span>
                    No reserve only
                  </button>
                </div>

                <div className="w-rail-g">
                  <div className="w-lbl">Price</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span className="w-field" style={{ flex: 1 }}>
                      $
                      <input
                        value={minRaw}
                        onChange={(e) => setMinRaw(e.target.value)}
                        placeholder="Min"
                        inputMode="decimal"
                        aria-label="Minimum price"
                      />
                    </span>
                    <span className="w-field" style={{ flex: 1 }}>
                      $
                      <input
                        value={maxRaw}
                        onChange={(e) => setMaxRaw(e.target.value)}
                        placeholder="Max"
                        inputMode="decimal"
                        aria-label="Maximum price"
                      />
                    </span>
                  </div>
                </div>

                {narrowed ? (
                  <button type="button" className="w-btn w-btn-out w-btn-sm" onClick={clearFilters}>
                    Clear{' '}
                    {activeCount > 0
                      ? `${String(activeCount)} filter${activeCount === 1 ? '' : 's'}`
                      : 'search'}
                  </button>
                ) : null}
              </aside>
            </div>

            {/*
            🔴 ONE RENDERER, TEN LAYOUTS (2026-09-14). This was a three-way
            ternary over 'stage' | 'grid' | 'table'. `WEB_LIVE_LAYOUTS` is keyed
            by `LiveRoomLayoutId`, so the build fails if the catalogue grows an
            id with no renderer.
          */}
            {/* The lots' container: what is drawn inside it, and on screen, is
                what the engine is told the bidder is looking at — and what may
                make a sound. */}
            <div className="lr-page-main" ref={lotsOnScreenRef}>
              {shown.length === 0 ? (
                <EmptyState
                  title="Nothing matches those filters"
                  body="Try widening the category, clearing the price range, or turning off no-reserve."
                  action={
                    <button type="button" className="w-btn w-btn-out" onClick={clearFilters}>
                      Clear filters
                    </button>
                  }
                />
              ) : (
                <>
                  {/* 🔴 THE ROW REPEATS ABOVE AND BELOW. At 100 per page a bidder
                    who has scrolled to the bottom should not have to scroll back
                    up to turn the page. Both rows are the same component driven
                    by the same state — the page owns it, so they cannot
                    disagree. `idSuffix` only keeps React keys unique. */}
                  {paginates ? (
                    <Pager
                      page={Math.min(page, pageCount)}
                      pageCount={pageCount}
                      pageSize={pageSize}
                      total={shown.length}
                      onPage={setPage}
                      onPageSize={setPageSize}
                      idSuffix="top"
                    />
                  ) : null}
                  {renderLayout()}
                  {paginates ? (
                    <Pager
                      page={Math.min(page, pageCount)}
                      pageCount={pageCount}
                      pageSize={pageSize}
                      total={shown.length}
                      onPage={setPage}
                      onPageSize={setPageSize}
                      idSuffix="bottom"
                    />
                  ) : null}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

/**
 * The countdown, in the room's compact form.
 *
 * Written here rather than through `<Countdown>` because the room already
 * re-renders every second off its own `tick`, and a second timer per lot on a
 * 200-lot table is 200 intervals doing the same job.
 */
